import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import DataObjectIcon from "@mui/icons-material/DataObject";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SettingsIcon from "@mui/icons-material/Settings";
import ShareIcon from "@mui/icons-material/Share";
import {
  WORKFLOW_STEPS,
  getDeepDDBase,
  getHistory,
  hubSpotRequest,
  runWorkflowStep,
} from "./deepDDApi";
import "./DeepDD.scss";

const SAMPLE_SUBJECT = {
  name: "Artem Politov",
  email: "a@gamma-it.com",
  position: "Purchase Director IT Hardware",
  company_name: "Gamma Group IT",
  website: "https://gamma-it.com",
  "LinkedIn person": "https://www.linkedin.com/in/artem-politov-91b3b1ab/",
  "LinkedIn company": "https://www.linkedin.com/company/gamma-it/about/",
  notes: "GAMMA GROUP was organized in 2008.",
};

const statusSeed = () =>
  WORKFLOW_STEPS.reduce((result, step) => {
    result[step.key] = step.key === "initialize" ? "ready" : "pending";
    return result;
  }, {});

function DeepDD() {
  const navigate = useNavigate();
  const [source, setSource] = useState("manual");
  const [hubSpotQuery, setHubSpotQuery] = useState("");
  const [jsonText, setJsonText] = useState(JSON.stringify(SAMPLE_SUBJECT, null, 2));
  const [investigationId, setInvestigationId] = useState("");
  const [statuses, setStatuses] = useState(statusSeed);
  const [activeStep, setActiveStep] = useState("");
  const [output, setOutput] = useState({ title: "Output", body: "Waiting for initialization." });
  const [outputOpen, setOutputOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [busy, setBusy] = useState(false);
  const pageSize = 5;

  const parsedSubject = useMemo(() => {
    try {
      return JSON.parse(jsonText);
    } catch (error) {
      return null;
    }
  }, [jsonText]);

  const refreshHistory = async () => {
    try {
      const data = await getHistory();
      setHistory(data.history || []);
    } catch (error) {
      setOutput({ title: "History unavailable", body: error.message });
    }
  };

  useEffect(() => {
    refreshHistory();
  }, []);

  const setStepStatus = (key, value) =>
    setStatuses((current) => ({ ...current, [key]: value }));

  const initialize = async () => {
    if (!parsedSubject) {
      setOutput({ title: "Invalid JSON", body: "Correct the subject JSON before initialization." });
      setOutputOpen(true);
      return;
    }

    setBusy(true);
    setInvestigationId("");
    setActiveStep("initialize");
    setStatuses({ ...statusSeed(), initialize: "running" });
    try {
      const data = await runWorkflowStep(WORKFLOW_STEPS[0], "", parsedSubject);
      setInvestigationId(data.investigation_id);
      setStepStatus("initialize", "complete");
      setOutput({ title: "Subject initialized", body: data });
      await refreshHistory();
      return data.investigation_id;
    } catch (error) {
      setStepStatus("initialize", "failed");
      setOutput({ title: "Initialization failed", body: error.message });
      setOutputOpen(true);
      return "";
    } finally {
      setBusy(false);
      setActiveStep("");
    }
  };

  const runStep = async (step) => {
    if (step.key === "initialize") {
      await initialize();
      return;
    }
    if (!investigationId) {
      setOutput({ title: "Initialize first", body: "Create the investigation before running modules." });
      return;
    }

    setBusy(true);
    setActiveStep(step.key);
    setStepStatus(step.key, "running");
    try {
      const data = await runWorkflowStep(step, investigationId, parsedSubject);
      setStepStatus(step.key, "complete");
      setOutput({ title: `${step.label} complete`, body: data });
      await refreshHistory();
    } catch (error) {
      setStepStatus(step.key, "failed");
      setOutput({ title: `${step.label} failed`, body: error.message });
      setOutputOpen(true);
    } finally {
      setBusy(false);
      setActiveStep("");
    }
  };

  const runRemaining = async () => {
    let currentId = investigationId;
    let initializedNow = false;
    if (!currentId) {
      initializedNow = true;
      currentId = await initialize();
      if (!currentId) return;
    }

    setBusy(true);
    try {
      for (const step of WORKFLOW_STEPS.slice(1)) {
        // A new investigation has no reusable step outputs, even if the
        // previous investigation left completed tiles in this render cycle.
        if (!initializedNow && statuses[step.key] === "complete") continue;

        setActiveStep(step.key);
        setStepStatus(step.key, "running");
        try {
          // Sequential execution is required because later steps consume earlier artifacts.
          // eslint-disable-next-line no-await-in-loop
          const data = await runWorkflowStep(step, currentId, parsedSubject);
          setStepStatus(step.key, "complete");
          setOutput({ title: `${step.label} complete`, body: data });
        } catch (error) {
          setStepStatus(step.key, "failed");
          setOutput({ title: `${step.label} failed`, body: error.message });
          setOutputOpen(true);
          break;
        }
      }
      await refreshHistory();
    } finally {
      setBusy(false);
      setActiveStep("");
    }
  };

  const pullHubSpot = async () => {
    if (!hubSpotQuery.trim()) return;
    setBusy(true);
    try {
      const data = await hubSpotRequest({ action: "run", scope: "one", query: hubSpotQuery, limit: 1 });
      const subject = data.subject || data.records?.[0];
      if (!subject) throw new Error("No matching HubSpot contact was found.");
      setJsonText(JSON.stringify(subject, null, 2));
      setOutput({ title: "HubSpot contact loaded", body: subject });
    } catch (error) {
      setOutput({ title: "HubSpot lookup failed", body: error.message });
      setOutputOpen(true);
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setInvestigationId("");
    setStatuses(statusSeed());
    setOutput({ title: "Output", body: "Waiting for initialization." });
    setOutputOpen(false);
  };

  const formatJson = () => {
    if (parsedSubject) setJsonText(JSON.stringify(parsedSubject, null, 2));
  };

  const pageCount = Math.max(1, Math.ceil(history.length / pageSize));
  const visibleHistory = history.slice((historyPage - 1) * pageSize, historyPage * pageSize);

  const loadHistory = (item) => {
    if (item.json) {
      try {
        setJsonText(JSON.stringify(JSON.parse(item.json), null, 2));
      } catch (error) {
        setOutput({ title: "Saved JSON is invalid", body: error.message });
        setOutputOpen(true);
      }
    }
    setInvestigationId(item.investigationId || item.id || "");
    setOutput({ title: item.title, body: item });
  };

  const downloadHistory = (item) => {
    console.log("downloadHistory_item==========",item);
    console.log("downloadHistory_getDeepDDBase==========",getDeepDDBase());
    console.log("downloadHistory_url==========",`${getDeepDDBase()}/ui/download_report.php?file=${encodeURIComponent(item.reportFile)}`);
    if (!item.reportFile && !item.reportUrl) return;
    const url = item.reportUrl;
    // const url = item.reportFile
    //   ? `${getDeepDDBase()}/ui/download_report.php?file=${encodeURIComponent(item.reportFile)}`
    //   : item.reportUrl;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const shareHistory = async (item) => {
    const text = item.reportUrl || item.json || "";
    if (navigator.share) {
      await navigator.share({ title: item.title, text, url: item.reportUrl || undefined });
    } else {
      await navigator.clipboard.writeText(text);
    }
  };

  return (
    <main className="page-container deepdd-page">
      <header className="deepdd-header">
        <div>
          <span className="deepdd-eyebrow">3D Intelligence</span>
          <h1>Deep Due Diligence</h1>
          <p>Investigate people and companies across compliance, reputation, ownership, and digital-risk sources.</p>
        </div>
        <button className="deepdd-button secondary" onClick={() => navigate("/DeepDD/settings")}>
          <SettingsIcon fontSize="small" /> Settings
        </button>
      </header>

      <section className="deepdd-workspace">
        <aside className="deepdd-card subject-card">
          <div className="card-heading">
            <div>
              <h2>Subject JSON</h2>
              <p>Use manual data or load one HubSpot contact.</p>
            </div>
          </div>

          <label className="field-label">
            Source
            <select value={source} onChange={(event) => setSource(event.target.value)}>
              <option value="manual">Manual JSON</option>
              <option value="hubspot">HubSpot CRM</option>
            </select>
          </label>

          {source === "hubspot" && (
            <div className="hubspot-lookup">
              <input
                value={hubSpotQuery}
                onChange={(event) => setHubSpotQuery(event.target.value)}
                placeholder="Email, company, domain, or contact name"
              />
              <button className="deepdd-button primary" onClick={pullHubSpot} disabled={busy}>
                Load CRM
              </button>
            </div>
          )}

          <textarea
            className="json-editor"
            value={jsonText}
            onChange={(event) => setJsonText(event.target.value)}
            spellCheck="false"
          />

          <div className="button-row">
            <button className="deepdd-button primary" onClick={initialize} disabled={busy}>
              <PlayArrowIcon fontSize="small" /> Initialize
            </button>
            <button className="deepdd-button secondary" onClick={formatJson}>
              <DataObjectIcon fontSize="small" /> Format
            </button>
            <button className="icon-action" title="Reset investigation" onClick={reset}>
              <RestartAltIcon fontSize="small" />
            </button>
          </div>
        </aside>

        <div className="deepdd-main">
          <section className="deepdd-card flow-card">
            <div className="card-heading flow-heading">
              <div>
                <h2>Investigation Flow</h2>
                <p>{investigationId ? `Investigation ${investigationId.slice(0, 8)}` : "Initialize a subject to begin."}</p>
              </div>
              <button className="deepdd-button success" onClick={runRemaining} disabled={busy}>
                <PlayArrowIcon fontSize="small" /> Run Remaining
              </button>
            </div>

            <div className="flow-progress">
              <span
                style={{
                  width: `${(Object.values(statuses).filter((value) => value === "complete").length / WORKFLOW_STEPS.length) * 100}%`,
                }}
              />
            </div>

            <div className="step-grid">
              {WORKFLOW_STEPS.map((step) => {
                const status = statuses[step.key];
                return (
                  <button
                    key={step.key}
                    className={`step-tile ${status}`}
                    onClick={() => runStep(step)}
                    disabled={busy && activeStep !== step.key}
                  >
                    <span className="step-number">
                      {status === "complete" ? <CheckIcon fontSize="inherit" /> : step.number}
                    </span>
                    <strong>{step.label}</strong>
                    <small>{status === "running" ? "Running..." : status}</small>
                  </button>
                );
              })}
            </div>
          </section>

          <section className={`deepdd-card output-card ${outputOpen ? "open" : ""}`}>
            <button className="output-heading" onClick={() => setOutputOpen((value) => !value)}>
              <span>
                <strong>{output.title}</strong>
                <small>{busy ? "Workflow is running." : "Technical output is collapsed by default."}</small>
              </span>
              <ExpandMoreIcon fontSize="small" />
            </button>
            {outputOpen && (
              <div className="output-body">
                <button
                  className="icon-action copy-output"
                  title="Copy output"
                  onClick={() => navigator.clipboard.writeText(
                    typeof output.body === "string" ? output.body : JSON.stringify(output.body, null, 2)
                  )}
                >
                  <ContentCopyIcon fontSize="small" />
                </button>
                <pre>{typeof output.body === "string" ? output.body : JSON.stringify(output.body, null, 2)}</pre>
              </div>
            )}
          </section>
        </div>
      </section>

      <section className="deepdd-card history-card">
        <div className="card-heading">
          <div>
            <h2>Investigation History</h2>
            <p>Previously initialized profiles and generated reports.</p>
          </div>
          <button className="deepdd-button secondary" onClick={refreshHistory}>Refresh</button>
        </div>

        <div className="history-table-wrap">
          <table>
            <thead>
              <tr><th>Profile</th><th>Updated</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {visibleHistory.map((item) => (
                <tr key={item.id}>
                  <td><strong>{item.title}</strong><small>ID {(item.id || "").slice(0, 8)}</small></td>
                  <td>{item.updatedAt || item.createdAt || "-"}</td>
                  <td><span className={`history-status ${item.reportUrl ? "ready" : ""}`}>{item.reportUrl ? "Report ready" : "JSON saved"}</span></td>
                  <td>
                    <div className="history-actions">
                      <button title="Load JSON" onClick={() => loadHistory(item)}><DataObjectIcon fontSize="small" /></button>
                      <button title="Download report" disabled={!item.reportUrl && !item.reportFile} onClick={() => downloadHistory(item)}><DownloadIcon fontSize="small" /></button>
                      <button title="Share" onClick={() => shareHistory(item)}><ShareIcon fontSize="small" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {!visibleHistory.length && <tr><td colSpan="4">No investigations found.</td></tr>}
            </tbody>
          </table>
        </div>
        <footer className="history-footer">
          <span>Showing {visibleHistory.length} of {history.length}</span>
          <div>
            <button disabled={historyPage <= 1} onClick={() => setHistoryPage((page) => page - 1)}><ChevronLeftIcon fontSize="small" /></button>
            <span>Page {historyPage} of {pageCount}</span>
            <button disabled={historyPage >= pageCount} onClick={() => setHistoryPage((page) => page + 1)}><ChevronRightIcon fontSize="small" /></button>
          </div>
        </footer>
      </section>
      <span className="api-location">API: {getDeepDDBase()}</span>
    </main>
  );
}

export default DeepDD;
